import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { OTPInput, SlotProps } from 'input-otp'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import { setSessionToken } from '~/features/auth'
import { LinkButton } from '~/features/navigation'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Alert, Icon, cn } from '@hap/components'

export const Route = createFileRoute('/_public/auth/code')({
  validateSearch: z.object({
    email: z.string(),
    redirect: z.string().optional(),
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const mutator = useMutation(trpc.session.verifyCode.mutationOptions())
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (code: string) => {
    mutator
      .mutateAsync({ email: search.email, code })
      .then((response) => {
        setSessionToken(response.token)
        // Only follow same-origin relative paths (e.g. a preserved OAuth
        // consent URL) — never an absolute URL someone slipped into search.
        if (search.redirect?.startsWith('/') && !search.redirect.startsWith('//')) {
          window.location.replace(search.redirect)
        } else {
          navigate({ to: '/', replace: true })
        }
      })
      .catch((error) => {
        setError(error.message || 'An error occurred while verifying the code.')
        console.error(error.message)
      })
  }, [])

  return (
    <PublicTemplate width={400}>
      <div className="bg-accent space-y-10 p-10 text-center">
        <header>
          <h1 className="text-2xl font-semibold">We emailed you a code</h1>
          <p className="text-accent-foreground">
            We sent an email to <strong className="font-semibold">{search.email}</strong>.
            If you don’t see the email, check your spam or junk folder.
          </p>
        </header>
        <div className="grid gap-2">
          <OTPInput
            maxLength={6}
            onComplete={handleSubmit}
            render={({ slots }) => (
              <div className="grid grid-cols-6 items-center gap-0">
                {slots.map((slot, idx) => (
                  <Slot key={idx} {...slot} />
                ))}
              </div>
            )}
          />

          {error && (
            <Alert variant="warn" className="mt-4">
              {error}
            </Alert>
          )}

          <div className="text-accent-foreground relative my-10 flex items-center border-t text-center">
            <span className="bg-accent absolute top-1/2 left-1/2 inline-block -translate-x-1/2 -translate-y-1/2 p-2">
              Or
            </span>
          </div>

          <div className="grid">
            <LinkButton to="/login" variant="secondary">
              <Icon name="arrow-left" /> &nbsp; Back to login options
            </LinkButton>
          </div>
        </div>
      </div>
    </PublicTemplate>
  )
}

function Slot(props: SlotProps) {
  return (
    <div
      className={cn(
        'relative h-20 font-mono text-[3rem]',
        'flex items-center justify-center',
        'transition-all duration-300',
        'border-input-secondary-background border-y border-r first:rounded-l-md first:border-l last:rounded-r-md',
        'group-hover:border-input-foreground/20 group-focus-within:border-input-background',
        'outline-hidden outline-0',
        {
          'bg-input-background border-input-focus-border z-10 shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))] outline-hidden':
            props.isActive,
        },
      )}
    >
      <div className="group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {props.char ?? props.placeholderChar}
      </div>
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  )
}

function FakeCaret() {
  return (
    <div className="animate-caret-blink pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="h-8 w-px bg-white" />
    </div>
  )
}
